import { supabase } from '@/lib/supabaseClient'
import type { Document } from '@/types'

const BUCKET_NAME = 'documents'
const ALLOWED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff']
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}

class DocumentService {
  /**
   * Upload a document to storage and create database record
   */
  async uploadDocument(
    submissionId: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<Document> {
    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      throw new Error('Invalid file type. Please upload PDF, PNG, JPEG, or TIFF files.')
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size exceeds 50MB limit.')
    }

    // Get user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    // Generate unique storage path
    const fileExt = file.name.split('.').pop()
    const fileName = `${submissionId}/${crypto.randomUUID()}.${fileExt}`

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (uploadError) {
      console.error('Error uploading file:', uploadError)
      throw new Error('Failed to upload file')
    }

    // Create document record
    const { data, error } = await supabase
      .from('documents')
      .insert({
        submission_id: submissionId,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        storage_path: fileName,
        status: 'pending'
      })
      .select()
      .single()

    if (error) {
      // Clean up uploaded file if database insert fails
      await supabase.storage.from(BUCKET_NAME).remove([fileName])
      console.error('Error creating document record:', error)
      throw new Error('Failed to create document record')
    }

    return data
  }

  /**
   * Get documents for a submission
   */
  async getDocuments(submissionId: string): Promise<Document[]> {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('submission_id', submissionId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching documents:', error)
      throw error
    }

    return data || []
  }

  /**
   * Get a signed URL for viewing a document
   */
  async getSignedUrl(storagePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(storagePath, 3600) // 1 hour expiry

    if (error) {
      console.error('Error creating signed URL:', error)
      throw error
    }

    return data.signedUrl
  }

  /**
   * Delete a document
   */
  async deleteDocument(documentId: string): Promise<void> {
    // Get document to get storage path
    const { data: document, error: fetchError } = await supabase
      .from('documents')
      .select('storage_path')
      .eq('id', documentId)
      .single()

    if (fetchError) {
      console.error('Error fetching document:', fetchError)
      throw fetchError
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([document.storage_path])

    if (storageError) {
      console.error('Error deleting from storage:', storageError)
      // Continue to delete database record even if storage delete fails
    }

    // Delete database record (this will cascade delete extracted_risk_fields)
    const { error: deleteError } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId)

    if (deleteError) {
      console.error('Error deleting document record:', deleteError)
      throw deleteError
    }
  }

  /**
   * Update document status
   */
  async updateDocumentStatus(
    documentId: string,
    status: Document['status'],
    errorMessage?: string
  ): Promise<Document> {
    const { data, error } = await supabase
      .from('documents')
      .update({
        status,
        error_message: errorMessage || null
      })
      .eq('id', documentId)
      .select()
      .single()

    if (error) {
      console.error('Error updating document status:', error)
      throw error
    }

    return data
  }

  /**
   * Download document file
   */
  async downloadDocument(storagePath: string): Promise<Blob> {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .download(storagePath)

    if (error) {
      console.error('Error downloading document:', error)
      throw error
    }

    return data
  }
}

export const documentService = new DocumentService()
